import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorContext, ErrorPagesService } from '../../../v2/errors/error-pages.service';

/** Failures in a row (status 0) before the server is asked whether it is down. */
export const MAX_CONSECUTIVE_FAILURES = 3;
/** A failure older than this no longer counts: the next one starts a new row. */
export const FAILURE_WINDOW_MS = 60_000;

/**
 * Decides when a failed request means Jet Fuel Tenders is down (Connection Lost) and when it was
 * one request that did not get through, which the page says in its own words.
 *
 * AACO F52 (staging, 2026-10-11): the count of status-0 failures used to live for the whole tab
 * (nothing reset it on success), so three blips hours apart opened the outage page on a healthy
 * server. Now a success clears the count, old failures expire, and the page opens only when the
 * health check (sent past the interceptor) fails too.
 */
@Injectable({
    providedIn: 'root'
})
export class ConnectionMonitorService {

    private consecutiveFailures = 0;
    private lastFailureAt = 0;
    private confirming = false;

    constructor(private errorPages: ErrorPagesService) { }

    /** Failures counted now (for tests and diagnostics). */
    public get failures(): number {
        return this.consecutiveFailures;
    }

    /** Any answer from our API: the connection works, the row of failures ends. */
    public recordSuccess(): void {
        this.consecutiveFailures = 0;
    }

    /** A request that never reached the server (status 0): counts towards an outage check. */
    public handleNetworkError(error: HttpErrorResponse, now = Date.now()): void {
        if (error.status !== 0) {
            return;
        }
        if (now - this.lastFailureAt > FAILURE_WINDOW_MS) {
            this.consecutiveFailures = 0;
        }
        this.lastFailureAt = now;
        this.consecutiveFailures++;
        if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            this.consecutiveFailures = 0;
            this.confirmOutage();
        }
    }

    /**
     * Opens Connection Lost only when the health check fails too; Scheduled Maintenance when it
     * answers 503. Up: nothing opens, the page keeps its own error and Try Again. One check at a
     * time, and none while the page already shows. `from` is read now, before anything moves.
     */
    public confirmOutage(context: Partial<ErrorContext> = {}): void {
        if (this.confirming || this.errorPages.showing === 'connection') {
            return;
        }
        const from = context.from ?? this.errorPages.currentUrl();
        this.confirming = true;
        this.errorPages.checkHealth().subscribe(state => {
            this.confirming = false;
            if (state === 'ok') {
                this.consecutiveFailures = 0;
                return;
            }
            this.errorPages.open(state === 'maintenance' ? 'maintenance' : 'connection', { ...context, from });
        });
    }
}
